import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { CoreSessionStorage } from '../../../storage/session/CoreSessionStorage';
import {
  acceptedBetAlreadyExists,
  areBetsEqual,
  countTotalBet,
  doubleBets,
  emptyBets,
  getNewBet,
} from '../../../utils/bets';

import type { AcceptedBet, BetPayloadType, BetsSliceType, BetType, IBetLimits, ILimit } from './types';

const initialState: BetsSliceType = {
  betList: null,
  betStack: [],
  disabledBetSpots: [],
  acceptedBets: [],
  lastBet: null,
  overridingTotalBet: null,
  betLimits: null,
  tableLimits: null,
  dragBuffer: null,
  updateTimestamp: 0,
};

export const betSlice = createSlice({
  name: 'bets',
  initialState,
  reducers: {
    initBetStack: (state, action: PayloadAction<number[]>) => {
      state.betList = action.payload;
    },
    resetBetStack: (state) => {
      state.betStack = [];
      state.overridingTotalBet = null;
    },
    setBetLimits: (state, action: PayloadAction<IBetLimits>) => {
      state.betLimits = action.payload;
    },
    setTableLimits: (state, action: PayloadAction<ILimit>) => {
      state.tableLimits = action.payload;
    },
    updateBetStackHead: (state, action: PayloadAction<BetType>) => {
      const betStack = state.betStack;
      const betStackHead = state.betStack[betStack.length - 1] || { betSpots: {} };
      const bet = action.payload;
      betStackHead.betSpots = Object.assign(betStackHead.betSpots, bet.betSpots);

      if (betStack.length > 0) {
        state.betStack[betStack.length - 1] = betStackHead;
      } else {
        state.betStack.push(betStackHead);
      }
    },
    overrideBetStack: (state, action: PayloadAction<BetType[]>) => {
      state.betStack = action.payload;
    },
    pushBet: (state, action: PayloadAction<BetPayloadType>) => {
      if (!state.betList) return;

      const { bet: changedBets, decimals } = action.payload;
      const currentBets = state.betStack[state.betStack.length - 1];
      const tableLimit = state.tableLimits?.max || Infinity;

      const newBet = getNewBet(
        currentBets,
        changedBets,
        decimals,
        state.betLimits || {},
        state.betList,
        tableLimit,
        action.payload.balance
      );
      if (!newBet) return;

      const totalBet = countTotalBet(newBet.betSpots, decimals);

      if (!areBetsEqual(newBet, currentBets) && action.payload.balance >= totalBet && totalBet <= tableLimit) {
        state.betStack.push(newBet);
        state.updateTimestamp = Date.now();
      }
    },
    clearBet: (state, action: PayloadAction<number>) => {
      if (!state.betList) {
        return;
      }
      const newBets = emptyBets();
      const currentBets = state.betStack[state.betStack.length - 1];

      state.betList.forEach((betId) => {
        newBets.betSpots[betId] = {
          value: betId === action.payload ? 0 : currentBets?.betSpots?.[betId]?.value || 0,
        };
      });

      state.betStack.push(newBets);
      state.updateTimestamp = Date.now();
    },
    popBet: (state) => {
      state.betStack.pop();
      state.updateTimestamp = Date.now();
    },
    wipeBet: (state, action: PayloadAction<number>) => {
      if (!state.betList && !state.betStack.length) {
        return;
      }
      state.betStack.forEach((bet) => {
        bet.betSpots[action.payload].value = 0;
      });
      if (state.lastBet) {
        const newLastBets = { ...state.lastBet.betSpots };
        newLastBets[action.payload].value = 0;
        if (Object.keys(newLastBets).some((betSpot) => newLastBets[betSpot].value)) {
          state.lastBet.betSpots = newLastBets;
        } else {
          state.lastBet = null;
        }
      }
    },
    doubleBet: (state, action: PayloadAction<{ balance: number; decimals: number }>) => {
      const { balance, decimals } = action.payload;
      const currentBets = state.betStack[state.betStack.length - 1];
      const betLimits = state.betLimits || {};
      const tableLimit = state.tableLimits?.max || Infinity;
      if (currentBets && state.betList) {
        const newBets = doubleBets(
          balance,
          decimals,
          currentBets,
          betLimits,
          tableLimit,
          state.overridingTotalBet || 0
        );
        const totalBet = newBets ? countTotalBet(newBets.betSpots, decimals) : 0;

        if (
          newBets &&
          !areBetsEqual(newBets, currentBets) &&
          Object.keys(newBets.betSpots).length > 0 &&
          totalBet <= tableLimit
        ) {
          state.betStack.push(newBets);
          state.updateTimestamp = Date.now();
        }
      }
    },
    saveLastBets: (state, action: PayloadAction<{ tableId: string }>) => {
      const currentBets = state.betStack[state.betStack.length - 1];
      if (currentBets) {
        state.lastBet = currentBets;
        CoreSessionStorage.setLastBets(currentBets, action.payload.tableId);
      }
    },
    initLastBets: (state, action: PayloadAction<{ bets: BetType }>) => {
      // Use only on game init
      state.lastBet = action.payload.bets;
    },
    reBet: (state, action: PayloadAction<{ balance: number; decimals: number }>) => {
      if (state.lastBet) {
        const { balance, decimals } = action.payload;
        const totalBet = countTotalBet(state.lastBet.betSpots, decimals);

        if (totalBet <= balance) {
          state.betStack.push(state.lastBet);
          state.updateTimestamp = Date.now();
        }
      }
    },
    setOverridingTotalBet: (state, action: PayloadAction<number>) => {
      state.overridingTotalBet = action.payload;
    },
    setAcceptedBets: (state, action: PayloadAction<AcceptedBet[]>) => {
      state.acceptedBets = action.payload;
    },
    pushAcceptedBet: (state, action: PayloadAction<AcceptedBet>) => {
      if (!acceptedBetAlreadyExists(state.acceptedBets, action.payload)) {
        state.acceptedBets.push(action.payload);
      }
    },
    updateAcceptedBet: (state, action: PayloadAction<number>) => {
      const betToUpdate = state.acceptedBets.find((bet) => bet.betcode === action.payload);
      if (betToUpdate) {
        betToUpdate.errorCode = 0;
      }
    },
    setDragBuffer: (state, action: PayloadAction<BetsSliceType['dragBuffer']>) => {
      state.dragBuffer = action.payload ? { ...action.payload, preliminary: true } : null;
    },
    setDisabledBetSpots: (state, action: PayloadAction<string[]>) => {
      state.disabledBetSpots = action.payload;
    },
    promoteDragBuffer: (state) => {
      if (state.dragBuffer) {
        state.dragBuffer.preliminary = false;
      }
    },
  },
});

export const betActions = betSlice.actions;
